import { useState, useEffect } from "react";
import Papa from "papaparse";
import * as XLSX from "xlsx";

const toCamelCase = (value) => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+(.)/g, (_, char) => char.toUpperCase());

  return normalized.charAt(0).toLowerCase() + normalized.slice(1);
};

const createCsvWorksheet = (rows) => {
  const worksheet = {};

  rows.forEach((row, rowIndex) => {
    row.forEach((value, columnIndex) => {
      const cellAddress = XLSX.utils.encode_cell({
        r: rowIndex,
        c: columnIndex,
      });
      worksheet[cellAddress] = { t: "s", v: value ?? "" };
    });
  });

  const lastRow = Math.max(rows.length - 1, 0);
  const lastColumn = Math.max(
    rows.reduce((maxColumns, row) => Math.max(maxColumns, row.length), 0) - 1,
    0,
  );

  worksheet["!ref"] = XLSX.utils.encode_range({
    s: { r: 0, c: 0 },
    e: { r: lastRow, c: lastColumn },
  });

  return worksheet;
};

export const parseImportedFile = (content, fileName) => {
  const normalizedName = (fileName || "").toLowerCase();

  if (normalizedName.endsWith(".csv")) {
    const parsed = Papa.parse(content, {
      skipEmptyLines: false,
    });

    const rows = Array.isArray(parsed.data)
      ? parsed.data.map((row) => (Array.isArray(row) ? row : [row]))
      : [];

    return [
      {
        name: "CSV",
        rows: rows.length,
        columns: Math.max(...rows.map((row) => row.length), 0),
        data: createCsvWorksheet(rows),
      },
    ];
  }

  const workbook = XLSX.read(content, { type: "binary" });
  const sheetNames = workbook.SheetNames;

  return sheetNames.map((sheetName) => {
    const worksheet = workbook.Sheets[sheetName];
    const range = XLSX.utils.decode_range(worksheet["!ref"]);

    return {
      name: sheetName,
      rows: range.e.r + 1,
      columns: range.e.c + 1,
      data: worksheet,
    };
  });
};

export const useExcelParser = (file) => {
  const [excelData, setExcelData] = useState(null);
  const [error, setError] = useState(null);
  const [parsedRows, setParsedRows] = useState(null);

  useEffect(() => {
    if (file) {
      const reader = new FileReader();

      reader.onload = (event) => {
        try {
          const content = event.target.result;
          const parsedData = parseImportedFile(content, file.name);

          if (parsedData && parsedData.length > 0) {
            setExcelData(parsedData);
            setError(null);
          } else {
            throw new Error("No data parsed");
          }
        } catch {
          setError("Failed to parse the file");
        }
      };

      reader.onerror = () => {
        setError("Failed to read the file");
      };

      if ((file.name || "").toLowerCase().endsWith(".csv")) {
        reader.readAsText(file);
      } else {
        reader.readAsBinaryString(file);
      }
    }
  }, [file]);

  const extractColumnData = (sheetIndex, columnIndex) => {
    if (excelData && excelData[sheetIndex]) {
      const sheet = excelData[sheetIndex].data;
      const rows = [];

      for (let r = 1; r < excelData[sheetIndex].rows; r++) {
        const rowData = {};
        const referenceValue =
          sheet[XLSX.utils.encode_cell({ r, c: columnIndex })]?.v;

        for (let c = 0; c < excelData[sheetIndex].columns; c++) {
          const cellAddress = XLSX.utils.encode_cell({ r, c });
          const cell = sheet[cellAddress];
          const headerCell = sheet[XLSX.utils.encode_cell({ r: 0, c })];
          const headerName = headerCell
            ? String(headerCell.v)
            : `column_${c + 1}`;
          rowData[toCamelCase(headerName)] = cell ? cell.v : "";
        }

        if (
          referenceValue !== undefined &&
          referenceValue !== null &&
          referenceValue !== ""
        ) {
          rowData.reference = referenceValue;
          rows.push(rowData);
        }
      }

      setParsedRows(rows);
    }
  };

  return { excelData, parsedRows, extractColumnData, error };
};
